import { useCapturePreviewContext } from '../capture-preview-context';
import type { DevelopmentPaceScenarioFormData } from '../types';
import { CaptureErrorNotification } from './capture-error-notification';
import { CaptureSummaryPanel } from './capture-summary-panel';
import { CoverageLine } from './coverage-line';

interface CaptureSectionProps {
  values: DevelopmentPaceScenarioFormData;
  /** Index of the selected area, or -1 when the Global tab is selected. */
  areaIndex: number;
}

/**
 * Live capture feedback for the DPS form. Reads the shared capture result from
 * {@link CapturePreviewProvider} (computed once for the whole region, so the chips and
 * the schedule preview never double-fetch), and renders the per-area capture panel for a
 * selected area plus the scenario-wide coverage line. A null result (no TC / no geometry)
 * yields the panel's prompt/empty states with no compute.
 *
 * @param {CaptureSectionProps} props - The form values and selected area index.
 * @return {JSX.Element | null} The capture feedback, or null on the Global tab before any
 *   result exists.
 */
export function CaptureSection({
  values,
  areaIndex,
}: CaptureSectionProps): JSX.Element | null {
  const { result, loading, error, retry, requestViewUnscheduled } =
    useCapturePreviewContext();

  // A failed compute must read as an error with a recovery path, not as a
  // legitimate empty capture. The hook clears `error` at the start of each run,
  // so a set `error` means the current definition's compute failed — surface it
  // instead of falling through to the empty/zero states below.
  if (error) {
    return <CaptureErrorNotification onRetry={retry} />;
  }

  if (areaIndex < 0) {
    // Global tab: only the scenario-wide coverage line is relevant here.
    return result ? (
      <CoverageLine
        coverage={result.coverage}
        onViewUnscheduled={requestViewUnscheduled}
      />
    ) : null;
  }

  const area = values.areas[areaIndex];
  const isSlotTarget = !!area && area.geometryType === 'slot' && !!area.slotId;
  // A slot target is a "chosen geometry" for the panel even though it has no
  // configurable id (its identity is the slot string).
  const hasGeometry = !!area && (area.configurableId !== null || isSlotTarget);
  const summary = result?.summaries[areaIndex] ?? null;

  return (
    <>
      <CaptureSummaryPanel
        summary={summary}
        loading={loading}
        hasGeometry={hasGeometry}
        isSlotTarget={isSlotTarget}
      />
      {result && (
        <CoverageLine
          coverage={result.coverage}
          onViewUnscheduled={requestViewUnscheduled}
        />
      )}
    </>
  );
}
